import { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Container } from '@mui/material';

// Common Components
import DynamicFavicon from './components/common/DynamicFavicon';
// import VisitTracker from './components/common/VisitTracker'; // DISABLED: собственная статистика отключена
import ScrollToTop from './components/common/ScrollToTop';
import CookieConsentBanner from './components/common/CookieConsentBanner';

// Layouts
import MainLayout from './components/layouts/MainLayout';
import AdminLayout from './components/layouts/AdminLayout';

// Pages
import HomePage from './pages/HomePage';
import GalleryPage from './pages/GalleryPage';
import PropertyPage from './pages/PropertyPage';
import BookingPage from './pages/BookingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import NotFoundPage from './pages/NotFoundPage';
import VideosPage from './pages/VideosPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ConsentPage from './pages/ConsentPage';
import TermsOfService from './pages/TermsOfService';
import TestimonialsPage from './pages/TestimonialsPage';
import AboutPage from './pages/AboutPage';
import SessionsPage from './pages/SessionsPage';
import UserSettings from './pages/UserSettings';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import TipsPage from './pages/TipsPage';
import TipPage from './pages/TipPage';
import ServicesList from './pages/ServicesList';
import ServiceDetails from './pages/ServiceDetails';
import MyCouponsPage from './pages/MyCouponsPage';
import CouponDetailPage from './pages/CouponDetailPage';
import UnsubscribePage from './pages/UnsubscribePage';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import PropertyManagement from './pages/admin/PropertyManagement';
import AdminPropertyForm from './pages/admin/PropertyForm';
import AdminUsers from './pages/admin/UsersManagement';
import AdminSlideshow from './pages/admin/AdminSlideshow';
import SiteSettings from './pages/admin/SiteSettings';
import Videos from './pages/admin/Videos';
import VideoForm from './pages/admin/VideoForm';
import Testimonials from './pages/admin/Testimonials';
import AdminManagement from './pages/admin/AdminManagement';
import BlogPosts from './pages/admin/BlogPosts';
import BlogPostForm from './pages/admin/BlogPostForm';
import BlogComments from './pages/admin/BlogComments';
import VisitStatistics from './pages/admin/VisitStatistics';
import Coupons from './pages/admin/Coupons';
import ScanCoupon from './pages/admin/ScanCoupon';
import AdvertisementList from './pages/admin/AdvertisementList';
import AdvertisementForm from './pages/admin/AdvertisementForm';
import AdvertisementSendResults from './pages/admin/AdvertisementSendResults';
import SubscribersList from './pages/admin/SubscribersList';
import Services from './pages/admin/Services';
import ServiceForm from './pages/admin/ServiceForm';
import Promotions from './pages/admin/Promotions';
import PromotionForm from './pages/admin/PromotionForm';
import PromotionPreview from './pages/admin/PromotionPreview';

// Auth Context
import { useAuthStore } from './store/authStore';

function App() {
  const { user, checkAuth, isLoading } = useAuthStore();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      await checkAuth();
      setInitialized(true);
    };

    initAuth();
  }, [checkAuth]);

  if (!initialized && isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <DynamicFavicon />
      {/* <VisitTracker /> DISABLED: собственная статистика отключена */}
      <ScrollToTop />
      <CookieConsentBanner />
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<MainLayout />}>
        <Route index element={<HomePage />} />
        <Route path="gallery" element={<GalleryPage />} />
        <Route path="property/:id" element={<PropertyPage />} />
        <Route path="booking" element={<BookingPage />} />
        <Route path="videos" element={<VideosPage />} />
        <Route path="testimonials" element={<TestimonialsPage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="blog" element={<BlogPage />} />
        <Route path="blog/:slug" element={<BlogPostPage />} />
        <Route path="tips" element={<TipsPage />} />
        <Route path="tips/:slug" element={<TipPage />} />
        <Route path="services" element={<ServicesList />} />
        <Route path="services/:slug" element={<ServiceDetails />} />
        <Route path="privacy" element={<PrivacyPolicy />} />
        <Route path="consent" element={<ConsentPage />} />
        <Route path="terms" element={<TermsOfService />} />
        <Route path="unsubscribe/:token" element={<UnsubscribePage />} />
        <Route path="sessions" element={user ? <SessionsPage /> : <Navigate to="/login" />} />
        <Route path="settings" element={user ? <UserSettings /> : <Navigate to="/login" />} />
        <Route path="my-coupons" element={user ? <MyCouponsPage /> : <Navigate to="/login" />} />
        <Route path="coupons/:id" element={user ? <CouponDetailPage /> : <Navigate to="/login" />} />
        <Route path="login" element={!user ? <LoginPage /> : <Navigate to="/" />} />
        <Route path="register" element={<Navigate to="/" />} />{/* DISABLED: регистрация отключена */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* Admin Routes */}
      <Route 
        path="/admin" 
        element={
          user && (user.role === 'admin' || user.role === 'superadmin') ? 
          <AdminLayout /> : 
          <Navigate to="/login" />
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="properties" element={<PropertyManagement />} />
        <Route path="properties/new" element={<AdminPropertyForm />} />
        <Route path="properties/edit/:id" element={<AdminPropertyForm />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="slideshow" element={<AdminSlideshow />} />
        <Route path="settings" element={<SiteSettings />} />
        <Route path="videos" element={<Videos />} />
        <Route path="videos/new" element={<VideoForm />} />
        <Route path="videos/edit/:id" element={<VideoForm />} />
        <Route path="testimonials" element={<Testimonials />} />
        <Route path="blog" element={<BlogPosts />} />
        <Route path="blog/new" element={<BlogPostForm />} />
        <Route path="blog/edit/:id" element={<BlogPostForm />} />
        <Route path="tips" element={<BlogPosts />} />
        <Route path="tips/new" element={<BlogPostForm />} />
        <Route path="tips/edit/:id" element={<BlogPostForm />} />
        <Route path="services" element={<Services />} />
        <Route path="services/add" element={<ServiceForm />} />
        <Route path="services/edit/:id" element={<ServiceForm />} />
        <Route path="blog-comments" element={<BlogComments />} />
        <Route path="statistics" element={<VisitStatistics />} />
        <Route path="coupons" element={<Coupons />} />
        <Route path="scan-coupon" element={<ScanCoupon />} />
        <Route path="advertisements" element={<AdvertisementList />} />
        <Route path="advertisements/create" element={<AdvertisementForm />} />
        <Route path="advertisements/edit/:id" element={<AdvertisementForm />} />
        <Route path="advertisements/results/:id" element={<AdvertisementSendResults />} />
        <Route path="subscribers" element={<SubscribersList />} />
        <Route path="promotions" element={<Promotions />} />
        <Route path="promotions/new" element={<PromotionForm />} />
        <Route path="promotions/edit/:id" element={<PromotionForm />} />
        <Route path="promotions/preview/:id" element={<PromotionPreview />} />
        <Route path="admin-management" element={
          user && user.role === 'superadmin' ? <AdminManagement /> : <Navigate to="/admin" />
        } />
      </Route>
    </Routes>
    </>
  );
}

export default App;
